import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Grid } from '@material-ui/core';
import { Badge } from '@onward-delivery/design/badge';
import { Card } from '@onward-delivery/design/card';
import { Progress } from '@onward-delivery/design/progress';
import { Switch } from '@onward-delivery/design/switch';
import { Tooltip } from '@onward-delivery/design/tooltip';
import {
    CheckCircle2 as PaidIcon,
    Clock as PendingIcon,
    AlertTriangle as IncompleteIcon,
} from '@onward-delivery/design/icons';
import { colors, typography } from '@/styles';

import { DesignScope, FILL_COLORS, FILL_LABELS, BORDER, GREY_TEXT, TOKENS, fillColor } from './blocks';
import { BILLING_STATES, FILL_STATES } from './dayMetrics';

// Capacity thresholds are ours, so `fillColor` stays here and the design components take a resolved
// colour. Progress clamps the bar but not the colour, so 130% reads Full and the badge shows 130%.
export const FillBar = ({ percent, size = 'md' }) => (
    <Progress value={percent || 0} size={size} color={fillColor(percent || 0)} />
);

export const PercentBadge = ({ percent, size, children }) => (
    <Badge appearance="tinted" size={size} color={fillColor(percent)}>
        {children}
    </Badge>
);

export const DayNumber = styled.span`
    ${typography.sansSerif}

    font-size: 14px;
    font-weight: 800;
    line-height: 20px;
`;

export const Meta = styled.span`
    ${typography.sansSerif}

    display: block;
    font-size: 11px;
    font-weight: 500;
    color: ${GREY_TEXT};
`;

export const ResourceName = styled.span`
    ${typography.sansSerif}

    display: block;
    font-size: 12px;
    font-weight: 700;
    color: #2b2b2b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    /* Without this the nowrap text sets a min-content floor its flex parent can't shrink below, so
       the card grows to fit the name instead of the name truncating. */
    min-width: 0;
`;

const TileValue = styled.div`
    ${typography.sansSerif}

    font-size: 22px;
    font-weight: 800;
    line-height: 26px;
    color: ${(props) => (props.accent ? TOKENS.primary : '#2b2b2b')};
`;

const TileLabel = styled.div`
    ${typography.sansSerif}

    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${GREY_TEXT};
`;

export const StatTile = ({ label, value, subtext, last, accent }) => (
    <Grid
        container
        direction="column"
        css={css`
            flex: 1;
            flex-basis: 0;
            padding: 16px 18px;
            position: relative;

            /* Inset rule rather than border-right, so the divider stops short of the card edges. */
            ${last
                ? ''
                : `
            ::after {
                content: '';
                position: absolute;
                top: 14px;
                bottom: 14px;
                right: 0;
                width: 1px;
                background-color: ${BORDER};
            }
            `}
        `}
    >
        <TileLabel>{label}</TileLabel>
        <TileValue accent={accent}>{value}</TileValue>
        {subtext ? <Meta>{subtext}</Meta> : null}
    </Grid>
);

const Swatch = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 2px;
    background-color: ${(props) => props.color};
    margin-right: 6px;
`;

// Applied via css rather than a styled wrapper so PrimaryButton keeps its theming.
export const buttonLabel = css`
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 0.06em;

    .MuiButton-label {
        align-items: center;
    }

    /* display: block is what levels the svg — inline svgs sit on the text baseline. */
    .MuiButton-startIcon {
        margin-left: 0;
        margin-right: 5px;
    }

    svg {
        display: block;
        width: 17px;
        height: 17px;
    }
`;

export const Legend = () => (
    <Grid container direction="column">
        <Grid
            container
            direction="row"
            css={css`
                align-items: center;
                flex-wrap: wrap;
                gap: 14px;
            `}
        >
            {[FILL_STATES.AVAILABLE, FILL_STATES.NEAR, FILL_STATES.FULL].map((state) => (
                <Grid
                    item
                    key={state}
                    css={css`
                        display: flex;
                        align-items: center;
                    `}
                >
                    <Swatch color={FILL_COLORS[state]} />
                    <Meta>{FILL_LABELS[state]}</Meta>
                </Grid>
            ))}
        </Grid>
        <Meta>Day counts = resources / stops / pieces · One bar per ceiling, across every resource on the day</Meta>
    </Grid>
);

// ── Per-resource cards ────────────────────────────────────────────────────────────────────────
//
// Not rendered by the month grid: its cell shows one aggregate card per day, which is the whole
// point of the rework. They are the week view's cell contents — one per resource with no cap — and
// the info dialog's samples. Kept here rather than deleted and restored, so the diff shows them
// moving out of the month cell rather than disappearing and coming back.

// Design `Card` ships unpadded; a grid cell needs a tighter scale than CardContent's p-4.
export const COMPACT_CARD = 'p-[6px_8px] rounded-[5px]';

// Card ships neutral / success / danger only, so INCOMPLETE shares UNPAID's red and differs by icon.
// PROCESSING and UNINVOICED take danger too, with the yellow override from WARNING_STATES on top.
//
// Two yellows and two reds, told apart by their icon rather than by a third colour: the clock is
// "waiting on someone else", the triangle is "waiting on us". So UNINVOICED and INCOMPLETE both
// take the triangle — nobody outside is going to raise that bill or close that order — while
// PROCESSING and UNPAID take the clock.
//
// UNINVOICED reads yellow rather than neutral. It is not settled and it is not on fire: a bill
// that has not gone out is normal for the last week or so, which is why it does not trip the
// Review badge until it ages either.
//
// Every BILLING_STATES value needs an entry in both maps. A missing one falls through to the PAID
// tick and silently reads as settled — which is exactly what UNINVOICED did when it was added.
export const BILLING_TONES = {
    [BILLING_STATES.PAID]: 'success',
    [BILLING_STATES.UNPAID]: 'danger',
    [BILLING_STATES.UNINVOICED]: 'danger',
    [BILLING_STATES.INCOMPLETE]: 'danger',
    [BILLING_STATES.PROCESSING]: 'danger',
    [BILLING_STATES.NONE]: 'neutral',
};

export const BILLING_ICONS = {
    [BILLING_STATES.UNPAID]: PendingIcon,
    [BILLING_STATES.UNINVOICED]: IncompleteIcon,
    [BILLING_STATES.INCOMPLETE]: IncompleteIcon,
    [BILLING_STATES.PROCESSING]: PendingIcon,
};

// The design package has no warning tone, so yellow is an override on top of `danger`. A set
// rather than a second boolean: the next state that wants yellow is one entry, not another branch.
const WARNING_STATES = new Set([BILLING_STATES.PROCESSING, BILLING_STATES.UNINVOICED]);

// Split out from the card below so the override reads as the one thing it is. The day's summary
// card deliberately does not take it — a month of toned cells with toned cards inside them was a
// wall of colour, so the day card stays neutral and the resource cards carry the signal.
export const billingCardTone = (billingState) => css`
    ${WARNING_STATES.has(billingState)
        ? `
        background-color: ${TOKENS.warning}20 !important;
        border-color: ${TOKENS.warning} !important;
        `
        : ''}
`;

export const PastResourceCard = ({ resource, billingState }) => {
    const Icon = BILLING_ICONS[billingState] || PaidIcon;
    const needsAttention = !!BILLING_ICONS[billingState];

    return (
        <Card
            tone={BILLING_TONES[billingState]}
            className={COMPACT_CARD}
            css={[
                css`
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: nowrap;
                    gap: 6px;
                    margin-bottom: 6px;
                `,
                billingCardTone(billingState),
            ]}
        >
            <ResourceName>{resource.name}</ResourceName>
            <span
                css={css`
                    display: flex;
                    align-items: center;
                    flex: 0 0 auto;
                    color: ${needsAttention ? colors.greys[1] : TOKENS.primary};

                    svg {
                        width: 15px;
                        height: 15px;
                    }
                `}
            >
                <Icon />
            </span>
        </Card>
    );
};

const fillTooltip = (fill) =>
    `Fullest ceiling · ${fill.ceiling.label} — ${fill.used.toLocaleString()} / ${fill.limit.toLocaleString()} ${
        fill.ceiling.short
    }`;

// `accepting: false` is a resource still working the day but closed to new bookings, so it stays on
// the cell, muted. The card itself is not a control: clicking the cell opens the day, whose side
// dialog holds the switch that closed it.
//
// `accepting` is a prop rather than a resourceDayState() call: the store resolves it once per
// resource-day now (R13), so the card must not re-derive it.
export const FutureResourceCard = ({ resource, fill, accepting = true }) => (
    <Card
        className={COMPACT_CARD}
        css={css`
            display: flex;
            flex-direction: column;
            /* Spaces the name row from the bar. FillBar renders a design Progress and forwards
               neither css nor className, so the gap has to live on the container. */
            gap: 4px;
            margin-bottom: 6px;

            ${accepting
                ? ''
                : `
            background-color: ${TOKENS.muted}14;
            border-color: ${TOKENS.border};
            `}
        `}
    >
        <Grid
            container
            direction="row"
            css={css`
                align-items: center;
                justify-content: space-between;
                flex-wrap: nowrap;
                gap: 6px;
            `}
        >
            <ResourceName
                css={css`
                    ${accepting ? '' : `color: ${TOKENS.muted};`}
                `}
            >
                {resource.name}
            </ResourceName>
            {/* flex: 0 0 auto, so a long name ellipsises instead of squeezing the badge out. */}
            {fill.state === FILL_STATES.UNCONFIGURED ? null : (
                <Tooltip content={fillTooltip(fill)} side="top">
                    <span
                        css={css`
                            flex: 0 0 auto;
                        `}
                    >
                        {fill.state === FILL_STATES.FULL ? (
                            <Badge appearance="tinted" color={FILL_COLORS[FILL_STATES.FULL]} tracking="0.04em">
                                FULL
                            </Badge>
                        ) : (
                            <PercentBadge percent={fill.percent}>{fill.percent}%</PercentBadge>
                        )}
                    </span>
                </Tooltip>
            )}
        </Grid>
        {fill.state === FILL_STATES.UNCONFIGURED ? (
            <Meta>No Ceilings Configured</Meta>
        ) : (
            <FillBar percent={fill.percent} size="sm" />
        )}
    </Card>
);

// Lives here rather than with the resource dialog that used to own it: the dialog is no longer
// where a day gets locked. The switch alone, with no label — the card that uses it puts the label
// on its own row, so the two cannot be one component.
//
// Switch ships only `md` (20px) and `lg` (30px). This wants the size between them, so it scales the
// smaller one rather than reaching into the component's thumb classes, which are not part of its
// API. Origin on the right so it grows away from the card edge it is aligned to.
const LOCK_SWITCH_SCALE = 1.25;

export const LockSwitch = ({ checked, disabled, onChange }) => (
    <DesignScope>
        <span
            css={css`
                display: inline-flex;
                transform: scale(${LOCK_SWITCH_SCALE});
                transform-origin: right center;
                opacity: ${disabled ? 0.6 : 1};
            `}
        >
            <Switch size="md" checked={!!checked} disabled={disabled} onCheckedChange={onChange} />
        </span>
    </DesignScope>
);
